import { useEffect, useState } from 'react';
import { Mail, Send, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { apiFetch } from '../../lib/apiClient';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseRecipients(text) {
  return text
    .split(/[,\n]/)
    .map((e) => e.trim())
    .filter(Boolean);
}

export default function DailyReportSettings() {
  const [recipientsText, setRecipientsText] = useState('');
  const [lastSent, setLastSent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);

  const applySettings = (data) => {
    setRecipientsText((data.recipients || []).join(', '));
    setLastSent(data.last_sent || null);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const res = await apiFetch('/api/admin/report-settings');
        if (res.ok) applySettings(await res.json());
      } catch (error) {
        console.error('Failed to load report settings:', error);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    const recipients = parseRecipients(recipientsText);
    const invalid = recipients.filter((e) => !EMAIL_PATTERN.test(e));
    if (recipients.length === 0 || invalid.length > 0) {
      toast.error(invalid.length ? `Invalid email: ${invalid.join(', ')}` : 'Add at least one recipient');
      return;
    }
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/report-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to save recipients');
      applySettings(data);
      toast.success('Report recipients saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save recipients');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      const res = await apiFetch('/api/admin/daily-report/send', { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to send report');
      setLastSent(data);
      toast.success(`Today's report sent to ${(data.recipients || []).join(', ')}`);
    } catch (error) {
      toast.error(error.message || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await apiFetch('/api/admin/daily-report/preview');
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to load preview');
      setPreviewHtml(data.html || '');
    } catch (error) {
      toast.error(error.message || 'Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-xl p-4 border mb-6" data-testid="daily-report-settings">
      <h2 className="font-semibold mb-3 flex items-center gap-2">
        <Mail className="w-4 h-4" />
        Daily report email
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        Sent every night at 11:30 PM (Toronto) with today's orders, money captured, profit, and what to
        Interac e-Transfer each food truck. Each report covers the current day only.
      </p>
      <div className="space-y-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Recipients (comma-separated)</label>
          <input
            type="text"
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            className="bg-muted rounded-lg px-3 py-2 text-sm w-full"
            data-testid="report-recipients-input"
          />
        </div>
        {lastSent?.sent_at && (
          <p className="text-xs text-muted-foreground">
            Last sent {new Date(lastSent.sent_at).toLocaleString('en-CA', { timeZone: 'America/Toronto' })}
            {lastSent.trigger ? ` (${lastSent.trigger})` : ''}
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save recipients'}
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={previewLoading}>
            <Eye className="w-4 h-4 mr-2" />
            {previewLoading ? 'Loading…' : 'Preview today'}
          </Button>
          <Button variant="outline" onClick={handleSendNow} disabled={sending} data-testid="send-report-now-btn">
            <Send className="w-4 h-4 mr-2" />
            {sending ? 'Sending…' : "Send today's report now"}
          </Button>
        </div>
      </div>

      {previewHtml && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setPreviewHtml('');
          }}
        >
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="font-semibold text-gray-900">Report preview</h3>
              <button onClick={() => setPreviewHtml('')} className="p-2 hover:bg-gray-100 rounded-lg" aria-label="Close preview">
                <X className="w-5 h-5 text-gray-700" />
              </button>
            </div>
            <iframe title="Daily report preview" srcDoc={previewHtml} className="w-full h-[70vh] border-0" sandbox="" />
          </div>
        </div>
      )}
    </div>
  );
}
